import {
  faChevronLeft,
  faChevronRight
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import usePagedScrolls, {
  leftSideSpacing
} from 'components/common/usePagedScrolls'
import React, { useEffect, useRef } from 'react'
import classNames from 'classnames'
import { twMerge } from 'tailwind-merge'
import { useWindowDimensions } from 'hooks'

export interface IProps {
  items: React.ReactElement[]
  itemWidth: number
  emptyStateContent?: React.ReactElement
}

function PageScroll({ items, itemWidth, emptyStateContent }: IProps) {
  const ref = useRef<HTMLDivElement>(null)

  const screen = useWindowDimensions()

  const {
    numberOfPages,
    scrollX,
    prevPage,
    nextPage,
    currentPage,
    setCurrentPage,
    updateCurrentPageMarker,
    setMaxScrollX
  } = usePagedScrolls({
    itemWidth,
    itemsCount: items.length
  })

  // if scrollX changed from button clicks
  useEffect(() => {
    ref.current?.scrollTo({ left: scrollX, behavior: 'smooth' })
  }, [itemWidth, scrollX])

  useEffect(() => {
    if (ref.current && screen.width) {
      setMaxScrollX(ref.current.scrollWidth - ref.current.clientWidth)
    }
  }, [screen.width, setMaxScrollX, updateCurrentPageMarker])

  //on scroll, update page marker
  useEffect(() => {
    const div = ref.current
    if (!div) {
      return
    }
    const scrollCb = () => {
      updateCurrentPageMarker(div.scrollLeft || 0)
    }
    div.addEventListener('scroll', scrollCb)
    return () => {
      div.removeEventListener('scroll', scrollCb)
    }
  }, [updateCurrentPageMarker])

  if (!items.length) {
    return emptyStateContent || null
  }

  return (
    <div className="w-screen relative -mx-10">
      <div className="hide-scrollbar overflow-auto pl-10 pb-10 z-10" ref={ref}>
        <div className={'w-full flex flex-row space-x-6'}>
          {items.map((item, i) => (
            <div
              key={i}
              className={twMerge(
                'shrink-0',
                i === items.length - 1 && 'pr-10' //hacky
              )}
              style={{
                width:
                  i === items.length - 1
                    ? itemWidth + leftSideSpacing //hacky
                    : itemWidth
              }}
            >
              {item}
            </div>
          ))}
        </div>
      </div>

      {currentPage > 0 && (
        <div
          className="h-14 w-14 rounded-full absolute left-5 top-[calc(50%-40px)] bg-gray-800/60 hover:bg-gray-800/90 flex justify-center items-center cursor-pointer z-20"
          onClick={prevPage}
        >
          <FontAwesomeIcon icon={faChevronLeft} size="lg" color="white" />
        </div>
      )}

      {currentPage < numberOfPages - 1 && (
        <div
          className="h-14 w-14 rounded-full absolute right-5 top-[calc(50%-40px)] bg-gray-800/60 hover:bg-gray-800/90 flex justify-center items-center cursor-pointer z-20"
          onClick={nextPage}
        >
          <FontAwesomeIcon icon={faChevronRight} size="lg" color="white" />
        </div>
      )}

      {numberOfPages > 1 && (
        <div className="w-full flex justify-center space-x-4 py-3 px-10 z-10">
          {Array(numberOfPages)
            .fill(numberOfPages)
            .map((_, index) => (
              <div
                key={`page-${index}`}
                onClick={() => setCurrentPage(index)}
                className={classNames(
                  'h-2 w-14 rounded-lg cursor-pointer border',
                  currentPage === index
                    ? 'bg-white border-gray-100 shadow'
                    : 'bg-gray-300 border-gray-300'
                )}
              />
            ))}
        </div>
      )}
    </div>
  )
}

export default PageScroll
